import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { CompetentAuthoritiesRepository } from '../../../../shared/states/competent-authorities.repository';
import { AnimalModel } from '../../models/animal.model';
import { AnimalQuarantineModel } from '../../models/medical/animal-quarantine.model';
import { getQuarantineReasonLabelKey } from '../../models/medical/medical.enums';
import { daysBetween, isoDatePart, todayIsoDate } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';
import { isQuarantineActive, isQuarantinePlanned } from '../../states/medical/animal-medical.selectors';

/** Lesesicht: aktive, geplante und beendete Quarantänen. */
@Component({
  selector: 'app-medical-quarantine',
  templateUrl: './medical-quarantine.component.html',
  styleUrls: ['../medical-shared.scss'],
  standalone: false
})
export class MedicalQuarantineComponent implements OnInit, OnDestroy {
  @Input() animal: AnimalModel | null = null;

  protected readonly reasonLabelKey = getQuarantineReasonLabelKey;

  active: AnimalQuarantineModel[] = [];
  planned: AnimalQuarantineModel[] = [];
  finished: AnimalQuarantineModel[] = [];
  today = todayIsoDate();

  private _authorityNames: Record<number, string> = {};
  private _userNames: Record<number, string> = {};
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _authoritiesRepository: CompetentAuthoritiesRepository
  ) {}

  ngOnInit(): void {
    combineLatest([
      this._medicalRepository.quarantines$,
      this._medicalRepository.userNames$,
      this._authoritiesRepository.getCompetentAuthorities(),
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([quarantines, userNames, authorities]) => {
        this.today = todayIsoDate();
        this._userNames = userNames;
        this._authorityNames = Object.fromEntries(authorities.map(authority => [authority.id, authority.name]));
        this.active = quarantines.filter(quarantine => isQuarantineActive(quarantine, this.today));
        this.planned = quarantines
          .filter(quarantine => isQuarantinePlanned(quarantine, this.today))
          .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
        this.finished = quarantines.filter(quarantine =>
          !this.active.includes(quarantine) && !this.planned.includes(quarantine)
        );
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  authorityName(quarantine: AnimalQuarantineModel): string {
    const id = refId(quarantine.authority);
    return id !== null ? this._authorityNames[id] ?? `#${id}` : '–';
  }

  creatorName(quarantine: AnimalQuarantineModel): string {
    const id = refId(quarantine.creator);
    return (id !== null && this._userNames[id]) || '–';
  }

  remainingDays(quarantine: AnimalQuarantineModel): number | null {
    const endsAt = isoDatePart(quarantine.endsAt);
    return endsAt ? daysBetween(this.today, endsAt) : null;
  }

  /** Anteil der verstrichenen Tage in Prozent, `null` bei unbefristeter Quarantäne. */
  progress(quarantine: AnimalQuarantineModel): number | null {
    const endsAt = isoDatePart(quarantine.endsAt);
    if (!endsAt) return null;
    const total = daysBetween(quarantine.startsAt, endsAt) + 1;
    const elapsed = daysBetween(quarantine.startsAt, this.today) + 1;
    return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
  }
}
